import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Palette,
  Wand2,
  HeartHandshake,
  Heart,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";
import { captureUtmFromUrl, captureLandingFingerprint, getStoredUtm } from "../lib/utm";
import { captureAmbassadorRefFromUrl } from "../lib/ambassadorRef";
import { trackAmbassadorClick } from "../lib/api";
import { pickHeroImage } from "../lib/startHeroImage";
import PrivacyIntro from "../components/PrivacyIntro";
import StartStepDemos from "../components/StartStepDemos";

/**
 * /start — Dedicated paid-traffic landing page.
 *
 * Optimised for TikTok cold clicks. Performance-marketing rules:
 *   1. Above-the-fold on a 390×844 iPhone: headline + subline + CTA
 *      MUST be visible without scrolling. Image is sized so it
 *      doesn't push the CTA below the fold.
 *   2. Lead with the OUTCOME the visitor saw in the ad
 *      ("learn makeup techniques tailored to your face") — not the
 *      technology (no AR / drag-mom / Trouvé jargon up top).
 *   3. ONE primary CTA. No nav, no carousel, no choice paralysis.
 *   4. How-it-works directly under the hero so on-fence visitors
 *      who scroll once get a 4-step path to "yes."
 *   5. Mission + community move BELOW conversion content — they
 *      reinforce, not gate, the signup.
 *
 * Funnel: TikTok click → /start (UTM captured) → CTA → privacy
 * intro modal → /login?signup=1 → onboarding → first routine.
 *
 * Demonstrative-hook upgrade (per founder, May 2026):
 *   • Hero shows an auto-playing muted product-demo video (full
 *     facial scan + personalised recs) instead of a static portrait.
 *     The hero image fallback stays as the <video> poster so cold
 *     loads on cellular still see something instantly.
 *   • Each How-It-Works step is now CLICKABLE — expands inline to
 *     a hands-on mini-demo (no auth required). Implemented in
 *     `components/StartStepDemos.jsx`.
 */

const Start = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showIntro, setShowIntro] = useState(null);

  // Resolve which hero variant to render BEFORE first paint so the
  // browser starts the image download with the correct URL. The
  // mapping reads `utm_campaign` (sessionStorage, captured on this
  // tab's first hit) and supports a `?hero=<slug>` URL override for
  // QA without polluting analytics with a real campaign tag.
  const hero = useMemo(() => {
    if (typeof window === "undefined") {
      return pickHeroImage(null, null);
    }
    // captureUtmFromUrl runs in the effect below, but we want the
    // campaign value NOW. Run it synchronously here — the function
    // is idempotent (first-write-wins) and cheap.
    captureUtmFromUrl();
    const params = new URLSearchParams(window.location.search);
    const manualOverride = params.get("hero");
    const utm = getStoredUtm();
    return pickHeroImage(utm?.utm_campaign, manualOverride);
    // location.search is the only thing that changes between renders
    // that matters here — recompute when the URL query changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Capture utm_* params from the URL into sessionStorage so every
  // event (signup, paywall_shown, checkout_succeeded, …) carries
  // attribution back to this campaign click. The hero useMemo above
  // already called captureUtmFromUrl synchronously; this redundant
  // call is the canonical analytics-side capture and stays for
  // clarity / future refactor safety.
  useEffect(() => {
    captureUtmFromUrl();
    // Capture http_referrer + landing_page_url ONCE per tab so the
    // founder can answer "where did this user come from?" without
    // joining events → users at admin lookup time. Idempotent.
    captureLandingFingerprint();
    // Ambassador referral capture — TikTok ambassadors send people to
    // /start?ref=alex. We persist the handle (first-touch wins,
    // 30-day TTL) so when the visitor eventually signs up, the
    // backend can attribute the conversion to Alex.
    const ref = captureAmbassadorRefFromUrl();
    if (ref) {
      // Fire-and-forget click counter. Backend ignores unknown
      // handles silently so a typo never 500s.
      trackAmbassadorClick(ref);
      track("ambassador_referral_landing", { handle: ref });
    }
    track("start_page_viewed", { hero_variant: hero.variant });
  }, [hero.variant]);

  // Authed users on /start get redirected straight to /home — they
  // don't need the marketing pitch.
  useEffect(() => {
    if (user) navigate("/home", { replace: true });
  }, [user, navigate]);

  const handleSignupClick = (e) => {
    e.preventDefault();
    track("start_page_cta_clicked", { hero_variant: hero.variant });
    setShowIntro(true);
  };

  const handleSecondaryCtaClick = () => {
    track("start_page_secondary_cta_clicked", { hero_variant: hero.variant });
    setShowIntro(true);
  };

  const handlePrivacyClose = () => {
    setShowIntro(null);
    navigate("/login?signup=1");
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #FDFBF7 0%, #F5E6E0 50%, #EAD2CB 100%)",
      }}
      data-testid="start-page"
    >
      {/* Decorative orbs — visual interest only, no distraction */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-24 h-[480px] w-[480px] rounded-full"
        style={{
          background:
            "radial-gradient(circle at 35% 35%, rgba(213,168,164,0.45), rgba(213,168,164,0) 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-32 h-[520px] w-[520px] rounded-full"
        style={{
          background:
            "radial-gradient(circle at 60% 60%, rgba(155,106,108,0.20), rgba(155,106,108,0) 70%)",
        }}
      />

      {/* Minimal brand mark, top-left only — no nav menu */}
      <div className="relative z-10 flex items-center gap-2.5 px-5 sm:px-10 pt-6 sm:pt-8">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center shadow-[0_4px_12px_rgba(155,106,108,0.28)]">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <span className="font-serif text-2xl tracking-tight text-[#2C2123] leading-none">
          TruBeauté
        </span>
      </div>

      {/* ── Hero ────────────────────────────────────────────────────
          Demo-led layout. The product video (autoplay muted loop) is
          the hero — it shows the actual scan + recommendation flow
          a TikTok visitor just saw 30 seconds ago. Static portrait
          stays as the <video poster> so first-paint is instant on
          slow cellular and we have a graceful fallback if a browser
          blocks autoplay (some iOS low-power modes do).

          Aspect ratio is 16:10 (matches the source) and the card is
          allowed to be ~62% width on mobile so the headline + CTA
          still sit above the fold on a 390×844 iPhone. */}
      <section className="relative z-10 px-5 sm:px-10 pt-6 sm:pt-16 pb-12 sm:pb-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.05, ease: "easeOut" }}
            className="relative w-64 sm:w-full sm:max-w-none mx-auto sm:mx-0 lg:order-2 aspect-[1280/612] rounded-[22px] overflow-hidden shadow-[0_20px_50px_-18px_rgba(155,106,108,0.45)] border border-white/40 bg-[#2C2123]"
            data-testid="start-hero-video-card"
          >
            <video
              src="/video/yc-demo-web.mp4"
              poster="/video/yc-demo-poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label={t("start.heroAlt")}
              className="w-full h-full object-cover"
              data-testid="start-hero-video"
              data-hero-variant={hero.variant}
            />
            {/* Tiny "LIVE DEMO" pill so visitors know this is the
                actual product, not a stock loop. Pinned to the
                top-left so it doesn't compete with focal action in
                the video. */}
            <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-black/55 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F5C28C] animate-pulse" />
              {t("start.heroLive")}
            </div>
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.18) 100%)",
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="lg:order-1"
          >
            {/* "For you" eyebrow — representation gets prominence (per
                founder hook spec). Sits BEFORE the headline so it's
                the first thing a visitor reads after the brand mark. */}
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-[#9B6A6C]/10 border border-[#9B6A6C]/25 text-[#5D4050] px-3 py-1 mb-4 sm:mb-5"
              data-testid="start-for-you-badge"
            >
              <Heart className="h-3 w-3 text-[#9B6A6C]" fill="currentColor" />
              <span className="text-[11px] sm:text-xs font-medium uppercase tracking-[0.16em]">
                {t("start.forYouEyebrow")}
              </span>
            </div>

            <h1
              className="font-serif text-[34px] leading-[1.08] sm:text-5xl lg:text-6xl text-[#2C2123] tracking-tight mb-4 sm:mb-5"
              data-testid="start-headline"
            >
              {t("start.headlineBefore")}{" "}
              <span className="italic text-[#9B6A6C]">
                {t("start.headlineEmphasis")}
              </span>
              {t("start.headlineAfter")}
            </h1>

            <p
              className="text-base sm:text-lg lg:text-xl text-[#5C4B51] leading-relaxed mb-3 sm:mb-4 max-w-xl"
              data-testid="start-subline"
            >
              {t("start.subline")}
            </p>

            <p
              className="text-sm sm:text-base text-[#9B6A6C] font-medium mb-7 sm:mb-9 max-w-xl"
              data-testid="start-supporting"
            >
              {t("start.supporting")}
            </p>

            <div className="flex flex-col items-stretch sm:items-start gap-3">
              <Link
                to="/login?signup=1"
                onClick={handleSignupClick}
                className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 text-base sm:text-lg font-medium transition-all shadow-[0_10px_28px_rgba(155,106,108,0.45)] hover:-translate-y-0.5 w-full sm:w-auto"
                data-testid="start-primary-cta"
              >
                {t("start.primaryCta")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="text-xs sm:text-sm text-[#8A7D82] sm:tracking-wide">
                {t("start.noCard")}
              </p>
              <Link
                to="/sample-routine"
                className="inline-flex items-center gap-1.5 text-sm text-[#5C4B51] hover:text-[#9B6A6C] underline-offset-4 hover:underline transition-colors"
                data-testid="start-sample-routine-link"
              >
                <Sparkles className="h-3.5 w-3.5" />
                {t("start.seeSampleRoutine")}
              </Link>
            </div>

            {/* Three benefit pills — outcome-first, no AR / drag-mom
                jargon. Each pill names a result the visitor saw in
                the TikTok creative (color correction, contour,
                confidence) so the page continues that conversation. */}
            <ul
              className="mt-8 sm:mt-10 flex flex-wrap gap-2 sm:gap-2.5 max-w-2xl"
              data-testid="start-benefit-pills"
            >
              {[
                { icon: Palette, label: t("start.pillCorrection") },
                { icon: Wand2, label: t("start.pillContour") },
                { icon: HeartHandshake, label: t("start.pillConfidence") },
              ].map((p) => (
                <li
                  key={p.label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/65 backdrop-blur-sm border border-white/70 text-[#5C4B51] px-3 py-1.5 text-xs sm:text-sm"
                >
                  <p.icon className="h-3.5 w-3.5 text-[#9B6A6C] shrink-0" />
                  <span className="leading-none">{p.label}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ── How It Works — INTERACTIVE STEPS ─────────────────────────
          Each step expands inline to a hands-on mini-demo so the
          visitor can FEEL the product before committing to signup.
          The component owns its own state; we just hand it the
          shell, the lead-in copy, and the secondary CTA below. */}
      <section
        className="relative z-10 px-5 sm:px-10 pb-14 sm:pb-20"
        data-testid="start-how-it-works"
      >
        <div className="max-w-5xl mx-auto">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#9B6A6C] mb-3 text-center">
            {t("start.howKicker")}
          </p>
          <h2 className="font-serif text-2xl sm:text-4xl text-[#2C2123] text-center mb-3 sm:mb-4 leading-tight">
            {t("start.howHeadline")}
          </h2>
          <p className="text-sm text-[#8A7D82] text-center mb-8 sm:mb-10 max-w-md mx-auto">
            {t("start.howSubline")}
          </p>

          <StartStepDemos />

          {/* Mid-page secondary CTA — picks up scrollers without making
              them flip back to the top. Same destination as the hero
              CTA, distinct event so we can read the conversion split. */}
          <div className="flex flex-col items-center mt-10 sm:mt-12">
            <button
              type="button"
              onClick={handleSecondaryCtaClick}
              className="group inline-flex items-center gap-2 bg-[#2C2123] text-white hover:bg-[#1A1416] rounded-full px-7 py-3.5 text-sm sm:text-base font-medium transition-all shadow-[0_8px_22px_rgba(44,33,35,0.30)] hover:-translate-y-0.5"
              data-testid="start-secondary-cta"
            >
              {t("start.primaryCta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-xs text-[#8A7D82] mt-3">{t("start.noCard")}</p>
          </div>
        </div>
      </section>

      {/* ── Mission + community ────────────────────────────────────
          Mission moves LAST so it supports conversion instead of
          gating it. Visitors who don't care about the why still got
          to the CTA; visitors who DO want to know "is this really
          for me" find the answer right where they go looking. */}
      <section
        className="relative z-10 px-5 sm:px-10 pb-20 sm:pb-24"
        data-testid="start-mission"
      >
        <div className="max-w-3xl mx-auto text-center bg-white/55 backdrop-blur-sm border border-white/70 rounded-3xl p-6 sm:p-10 soft-shadow">
          <p className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#9B6A6C] mb-3">
            {t("start.missionKicker")}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2C2123] mb-4 leading-tight">
            {t("start.missionHeadline")}
          </h2>
          <p className="text-sm sm:text-base text-[#5C4B51] leading-relaxed">
            {t("start.missionBody")}
          </p>
        </div>
      </section>

      {/* Lightweight footer — just the legal links the user needs to
          trust the page. NO main-site nav so we don't lose them
          mid-funnel. */}
      <footer className="relative z-10 px-5 sm:px-10 pb-10 pt-2">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#8A7D82]">
          <span>© TruBeauté</span>
          <Link
            to="/privacy"
            className="hover:text-[#5C4B51] transition-colors"
            data-testid="start-footer-privacy"
          >
            {t("start.footerPrivacy")}
          </Link>
          <Link
            to="/terms"
            className="hover:text-[#5C4B51] transition-colors"
            data-testid="start-footer-terms"
          >
            {t("start.footerTerms")}
          </Link>
          <Link
            to="/"
            className="hover:text-[#5C4B51] transition-colors"
            data-testid="start-footer-full-site"
          >
            {t("start.footerFullSite")}
          </Link>
        </div>
      </footer>

      {/* Privacy intro modal — fires only when the user clicks the
          CTA, never on first paint. */}
      <PrivacyIntro forceOpen={showIntro} onClose={handlePrivacyClose} />
    </div>
  );
};

export default Start;
